<template>
  <section class="max-w-screen-3xl mx-auto px-4 sm:px-6 lg:px-8 2xl:px-22">
    <div class="relative h-[528px] overflow-hidden bg-primary rounded-2xl">
      <!-- Background Image -->
      <div class="absolute inset-0">
        <img
          :src="isMobile ? mobileImageUrl : backgroundImage"
          :alt="title"
          class="absolute inset-0 w-full h-full object-cover"
          :style="isMobile ? {} : { objectPosition: 'right' }"
        />
      </div>

      <!-- Golden Gradient Overlay -->
      <div
        class="absolute inset-0"
        :style="isMobile ? mobileGradientStyle : desktopGradientStyle"
      />

      <!-- Content -->
      <div
        class="relative z-10 h-full px-4 md:px-12 lg:px-16 flex items-end md:items-center"
      >
        <div class="flex flex-col gap-12 pb-10 md:pb-0">
          <!-- Text Content -->
          <div class="flex flex-col gap-4">
            <h2
              class="max-w-60 md:max-w-sm text-3xl md:text-5xl lg:text-[40px] font-semibold font-outfit text-white leading-[120%] tracking-[-0.68px]"
            >
              {{ title }}
            </h2>
            <p
              class="text-xl font-medium font-outfit text-white/80 leading-[140%] max-w-md"
            >
              {{ description }}
            </p>
          </div>

          <!-- CTA Buttons -->
          <div class="flex flex-wrap items-center gap-4">
            <!-- Contact Button -->
            <NuxtLink
              :to="contactLink"
              class="inline-flex items-center gap-2 justify-center bg-white hover:bg-primary-light transition-colors rounded-[30px] px-6 py-4 shadow-sm leading-none w-full md:w-auto"
            >
              <span class="text-[#a68235] text-lg font-semibold font-outfit">
                Contactez-nous
              </span>
            </NuxtLink>

            <!-- WhatsApp Button -->
            <a
              :href="whatsappLink"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] leading-none transition-colors rounded-[28px] px-6 py-4 min-w-[205px] justify-center w-full md:w-auto"
            >
              <svg
                class="w-5 h-5 text-white"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"
                />
              </svg>
              <span class="text-white text-lg font-semibold font-outfit">
                Ouvrir WhatsApp
              </span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";

interface Props {
  /** Section title */
  title?: string;
  /** Section description */
  description?: string;
  /** Background image URL */
  backgroundImage?: string;
  /** Mobile background image URL */
  mobileImageUrl?: string;
  /** Contact page link */
  contactLink?: string;
  /** WhatsApp link */
  whatsappLink?: string;
}

const props = withDefaults(defineProps<Props>(), {
  title: "Besoin de conseils personnalisés ?",
  description:
    "Notre équipe est à votre disposition pour vous aider à choisir le plan d'épargne le plus adapté à votre situation.",
  backgroundImage: "/images/cta-agent.webp",
  mobileImageUrl: "/images/cta-agent-mobile.webp",
  contactLink: "/contact",
  whatsappLink: "https://wa.me/32471647432",
});

const isMobile = ref(false);

// Desktop gradient (horizontal)
const desktopGradientStyle = {
  background:
    "linear-gradient(90deg, rgb(166, 130, 53) 0%, rgb(208, 164, 68) 32.215%, rgba(208, 164, 68, 0.8) 51.819%, rgba(208, 164, 68, 0) 100%)",
};

// Mobile gradient (vertical)
const mobileGradientStyle = {
  background:
    "linear-gradient(rgba(208, 164, 68, 0) 0%, rgba(208, 164, 68, 0.8) 35.212%, rgb(208, 164, 68) 80.397%, rgb(166, 130, 53) 100%)",
};

onMounted(() => {
  const checkMobile = () => {
    isMobile.value = window.innerWidth < 768;
  };

  checkMobile();
  window.addEventListener("resize", checkMobile);

  return () => window.removeEventListener("resize", checkMobile);
});
</script>
